import "./program-comparison-tray.scss";

import cn from "clsx";
import { Icon } from "design-system/components/icons/icon";
import { CtaLink } from "design-system/components/primitives/cta-link/cta-link";
import { useProgramComparison } from "design-system/hooks/use-program-comparison";
import {
  ContentReferencesEntry,
  OnlineContentReferencesEntry,
} from "frontend/contentful/schema/universal";
import { DataLayerEvent, useDataLayer } from "frontend/hooks/use-data-layer";
import { useState } from "react";
import { usePageProps } from "frontend/hooks/use-page-props";

export interface ProgramComparisonTrayProps {
  programFinderUrl?: string;
  isHBSOnline?: boolean;
  contentReferences?: ContentReferencesEntry | OnlineContentReferencesEntry;
  dataLayer?: DataLayerEvent;
  requestInfoUrl?: string;
}

export function ProgramComparisonTray({
  programFinderUrl,
  isHBSOnline,
  requestInfoUrl,
  /* eslint-disable-next-line camelcase */
  dataLayer = { event: "program_finder_result_click", event_data: {} },
}: ProgramComparisonTrayProps) {
  const pageProps = usePageProps();
  const [isTrayHidden, setIsTrayHidden] = useState(false);

  const { programsToCompare, removeProgramToCompare } = useProgramComparison();

  const { pushDataLayer } = useDataLayer();

  // Show the tray when adding new programs to compare
  const [prevProgramsLength, setPrevProgramsLength] = useState(
    programsToCompare.length,
  );
  if (prevProgramsLength !== programsToCompare.length) {
    setPrevProgramsLength(programsToCompare.length);

    if (prevProgramsLength < programsToCompare.length) {
      setIsTrayHidden(false);
    }
  }

  if (programsToCompare.length < 1) {
    return null;
  }

  const trayClass = cn(
    "hbs-program-comparison-tray__inner",
    isTrayHidden && "hbs-program-comparison-tray__inner--hidden",
    isHBSOnline && "hbs-program-comparison-tray__inner--online",
  );

  const programIds = programsToCompare.map((p) => p.id).join(",");
  let currentPageUrl: URL | undefined;
  try {
    let url = pageProps.currentPage?.url || window.location.href;
    if (!url.startsWith("https://")) {
      url = `https://${url}`;
    }
    currentPageUrl = url ? new URL(url) : undefined;
  } catch {
    currentPageUrl = undefined;
  }

  const currentPageHostname = currentPageUrl?.hostname;
  const isExEdDomain =
    currentPageHostname === "exed.hbs.edu" ||
    currentPageHostname?.endsWith(".exed.hbs.edu");

  const isOnlineDomain =
    currentPageHostname === "online.hbs.edu" ||
    currentPageHostname?.endsWith("online.hbs.edu");

  const defaultFinderUrl = isExEdDomain
    ? "/programs"
    : isOnlineDomain
      ? "/courses"
      : "https://www.exed.hbs.edu/programs";

  const pageUrl = new URL(
    programFinderUrl || defaultFinderUrl,
    typeof window !== "undefined"
      ? window.location.origin
      : currentPageUrl?.origin || "https://www.exed.hbs.edu",
  );
  if (programIds) {
    pageUrl.searchParams.set("programs", programIds);
  }
  const envParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("environment")
      : undefined;
  if (envParam) {
    pageUrl.searchParams.set("environment", envParam);
  }

  return (
    <div
      className={cn(
        "hbs-program-comparison-tray",
        isHBSOnline && "hbs-program-comparison-tray--online",
      )}
    >
      <h2 className="hbs-global-visually-hidden">
        Compare {isHBSOnline ? "Courses" : "Programs"}
      </h2>
      <button
        onClick={() => setIsTrayHidden(false)}
        className={cn(
          "hbs-program-comparison-tray__fab",
          !isTrayHidden && "hbs-program-comparison-tray__fab--hidden",
        )}
      >
        <Icon
          name="compare"
          className="hbs-program-comparison-tray__fab-icon"
        />
        <span className="hbs-global-visually-hidden">Show comparison tray</span>
      </button>
      <div className={trayClass}>
        <button
          onClick={() => setIsTrayHidden(true)}
          className="hbs-program-comparison-tray__hide"
          aria-label="Hide comparison tray"
        >
          Hide
          <Icon name="close" />
        </button>

        <div className="hbs-program-comparison-tray__track">
          <ul className="hbs-program-comparison-tray__list">
            {programsToCompare.map((program) => (
              <li
                key={program.id}
                className="hbs-program-comparison-tray__item"
              >
                <p className="hbs-program-comparison-tray__item-title">
                  {program.title}
                </p>
                <button
                  onClick={() => {
                    removeProgramToCompare(program);
                    /* eslint-disable camelcase */
                    if (!dataLayer.event) {
                      dataLayer.event = "program_finder_result_click";
                    }
                    if (dataLayer.event_data) {
                      dataLayer.event_data.program_action =
                        "compare remove click";
                      dataLayer.event_data.program_name = program.title;
                      pushDataLayer(dataLayer);
                    } else {
                      dataLayer.event_data = {
                        program_action: "compare remove click",
                        program_name: program.title,
                      };
                    }
                    pushDataLayer(dataLayer);
                    /* eslint-enable camelcase */
                  }}
                  className="hbs-program-comparison-tray__remove"
                >
                  <span className="hbs-global-visually-hidden">
                    Remove {program.title}
                  </span>
                  <Icon name="close" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="hbs-program-comparison-tray__actions">
          <CtaLink
            className={cn(
              "hbs-program-comparison-tray__compare",
              programsToCompare.length < 2 &&
                "hbs-program-comparison-tray__compare--disabled",
            )}
            href={pageUrl.toString()}
            isSmall={true}
            type={"primary-button"}
          >
            Compare {isHBSOnline ? "Courses" : "Programs"}
          </CtaLink>
          {isHBSOnline && requestInfoUrl && (
            <CtaLink
              className="hbs-program-comparison-tray__compare"
              href={requestInfoUrl}
              isSmall={true}
              type={"secondary-button"}
              icon="arrow"
            >
              Request Info
            </CtaLink>
          )}
        </div>
      </div>
    </div>
  );
}
